import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import CustomModal from '../../../../../components/custom-modal/custom-modal';
import {RiskChip} from '../../../../../components/risk-chip/riskChip';
import {getScoringReference} from '../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import {getScoringReferenceState} from '../../../../../redux/selectors/riskAssessmentCore';
import {impactLabel, RISK_LEVEL_STOP} from '../../../../../utils/cybersecurityScoring';
import './scoringGuide.scss';

// operational and privacy carry their guidance as per-level items rather than one line
function CategoryGuidance({category}) {
  if (category.guidance_items?.length > 0) {
    return (
      <ul className="scoring-guide__guidance-list">
        {category.guidance_items.map(item => (
          <li key={item.level}>
            <strong>{item.level}:</strong> {item.text}
          </li>
        ))}
      </ul>
    );
  }
  return <>{category.guidance ?? ''}</>;
}

function ImpactSection({reference}) {
  const {impact_levels: levels, impact_categories: categories} = reference;
  return (
    <section className="scoring-guide__section">
      <div className="scoring-guide__section-title">Impact Evaluation Criteria</div>
      <div className="scoring-guide__table-wrap">
        <table className="scoring-guide__table">
          <thead>
            <tr>
              <th />
              {categories.map(category => (
                <th key={category.key}>{category.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {levels.map(level => (
              <tr key={level}>
                <th>{impactLabel(reference, level)}</th>
                {categories.map(category => (
                  <td key={category.key}>
                    {category.criteria?.[level] ?? ''}
                    {category.examples?.[level] && (
                      <p className="scoring-guide__examples">
                        <strong>Examples:</strong> {category.examples[level]}
                      </p>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <th>Guidelines for Determining the Evaluation Score</th>
              {categories.map(category => (
                <td key={category.key}>
                  <CategoryGuidance category={category} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FeasibilityFactor({factor}) {
  const hasCriteria = factor.criteria && Object.keys(factor.criteria).length > 0;
  const hasExamples = factor.examples && Object.keys(factor.examples).length > 0;
  return (
    <div className="scoring-guide__factor">
      <div className="scoring-guide__factor-title">{factor.label}</div>
      <div className="scoring-guide__table-wrap">
        <table className="scoring-guide__table">
          <thead>
            <tr>
              <th>Option</th>
              <th className="scoring-guide__score">Score</th>
              {hasCriteria && <th>Criteria</th>}
              {hasExamples && <th>Examples</th>}
            </tr>
          </thead>
          <tbody>
            {factor.options.map(option => (
              <tr key={option.code}>
                <td>{option.label}</td>
                <td className="scoring-guide__score">{option.score}</td>
                {hasCriteria && <td>{factor.criteria?.[option.code] ?? ''}</td>}
                {hasExamples && <td>{factor.examples?.[option.code] ?? ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {factor.guidance && (
        <>
          <p className="scoring-guide__guidance">
            <strong>Guidelines for Determining Evaluation Scores:</strong> {factor.guidance}
          </p>
        </>
      )}
    </div>
  );
}

// lower bound of each band is one past the previous band's max; the first
// band starts at zero and a null max renders as "or more"
function thresholdRows(thresholds) {
  let lower = 0;
  return thresholds.map(({max, rating}) => {
    const range = max === null ? `${lower} or more` : `${lower}–${max}`;
    lower = max === null ? lower : max + 1;
    return {range, rating};
  });
}

function FeasibilitySection({reference}) {
  const rows = thresholdRows(reference.feasibility_thresholds);
  return (
    <section className="scoring-guide__section">
      <div className="scoring-guide__section-title">Attack Feasibility Criteria</div>
      {reference.feasibility_factors.map(factor => (
        <FeasibilityFactor key={factor.key} factor={factor} />
      ))}
      <div className="scoring-guide__factor-title">Total Score to Rating</div>
      <div className="scoring-guide__table-wrap">
        <table className="scoring-guide__table">
          <thead>
            <tr>
              <th>Total</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.rating}>
                <td>{row.range}</td>
                <td>{row.rating}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RiskSection({reference}) {
  const riskValues = Object.keys(reference.risk_levels).sort((a, b) => Number(b) - Number(a));
  return (
    <section className="scoring-guide__section">
      <div className="scoring-guide__section-title">Risk Value and Response</div>
      <div className="scoring-guide__factor-title">Risk Value Determination Matrix</div>
      <div className="scoring-guide__table-wrap">
        <table className="scoring-guide__table scoring-guide__table--matrix">
          <colgroup>
            <col className="scoring-guide__col--axis-vertical" />
            <col className="scoring-guide__col--axis" />
            {reference.feasibility_ratings.map(rating => (
              <col key={rating} className="scoring-guide__col--rating" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="is-blank" />
              <th className="is-blank" />
              <th colSpan={reference.feasibility_ratings.length}>Attack Feasibility Rating</th>
            </tr>
            <tr>
              <th className="is-blank" />
              <th className="is-blank" />
              {reference.feasibility_ratings.map(rating => (
                <th key={rating}>{rating}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reference.impact_levels.map((level, index) => (
              <tr key={level}>
                {index === 0 && (
                  <th className="is-axis is-axis-vertical" rowSpan={reference.impact_levels.length}>
                    Impact Evaluation
                  </th>
                )}
                <th>{impactLabel(reference, level)}</th>
                {reference.feasibility_ratings.map(rating => {
                  const value = reference.risk_matrix?.[level]?.[rating];
                  return (
                    <td key={rating} data-stop={RISK_LEVEL_STOP[value]}>
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="scoring-guide__factor-title">Response Policy by Risk</div>
      <div className="scoring-guide__table-wrap scoring-guide__table-wrap--policies">
        <table className="scoring-guide__table">
          <thead>
            <tr>
              <th>Risk value</th>
              <th>Response policy</th>
              <th>Deliberation</th>
            </tr>
          </thead>
          <tbody>
            {riskValues.map(value => (
              <tr key={value}>
                <td>
                  <RiskChip value={Number(value)} level={reference.risk_levels[value]} />
                </td>
                <td>{reference.response_policies?.[value]}</td>
                <td>{reference.deliberations?.[value]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function ScoringGuide({show, setShow}) {
  const dispatch = useDispatch();
  const cachedReference = useSelector(getScoringReferenceState());
  const reference = Array.isArray(cachedReference?.impact_categories) ? cachedReference : null;

  useEffect(() => {
    if (show) {
      dispatch(getScoringReference({}));
    }
  }, [show]);

  return (
    <CustomModal title="Scoring Guide" show={show} setShow={setShow} maxWidth="md"
      style={{minWidth: '900px', maxWidth: '1300px', maxHeight: '78vh'}}>
      {reference && (
        <div className="scoring-guide">
          <ImpactSection reference={reference} />
          <FeasibilitySection reference={reference} />
          <RiskSection reference={reference} />
        </div>
      )}
    </CustomModal>
  );
}
